<template>
  <div class="deal-email-thread">
    <div v-if="loading" class="deal-email-thread__loading">
      <LoadingSpinner />
    </div>
    <EmptyState
      v-else-if="!thread"
      size="sm"
      :title="$t('deals.mail_thread_empty')"
      :description="$t('deals.mail_thread_empty_description')"
    />
    <div v-else class="deal-email-thread__messages">
      <div
        v-for="message in thread.messages"
        :key="message.id"
        class="deal-email-thread__msg"
        :class="`deal-email-thread__msg--${message.direction}`"
      >
        <div class="deal-email-thread__msg-header">
          <div
            class="deal-email-thread__msg-icon"
            :class="`deal-email-thread__msg-icon--${message.direction}`"
          >
            <BaseIcon :name="messageIcon(message)" size="14" />
          </div>
          <div class="deal-email-thread__msg-meta">
            <span class="deal-email-thread__msg-from">
              {{ message.from_name || message.from_email || $t('deals.mail_unknown_sender') }}
            </span>
            <span class="deal-email-thread__msg-time">
              {{ formatDateTimeEU(message.created_at) }}
            </span>
          </div>
        </div>
        <pre v-if="messageBody(message)" class="deal-email-thread__msg-body">{{ messageBody(message) }}</pre>
        <div v-if="messageImages(message).length" class="deal-email-thread__images">
          <a
            v-for="image in messageImages(message)"
            :key="image.url"
            :href="image.url"
            target="_blank"
            rel="noopener noreferrer"
            class="deal-email-thread__image"
          >
            <img :src="image.url" :alt="image.filename || $t('deals.mail_photo')" />
          </a>
        </div>
        <div v-if="messageFiles(message).length" class="deal-email-thread__files">
          <a
            v-for="file in messageFiles(message)"
            :key="file.url"
            :href="file.url"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ file.filename || $t('deals.mail_attachment') }}
          </a>
        </div>
      </div>
    </div>
    <div class="deal-email-thread__actions">
      <Btn variant="outlined" @click="handleClose">
        {{ $t('common.close') }}
      </Btn>
      <Btn
        v-if="thread && canReply"
        variant="primary"
        icon="envelope"
        @click="handleReply"
      >
        {{ $t('deals.mail_reply') }}
      </Btn>
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted, getCurrentInstance } from 'vue';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import { dealEmailService } from '@/services/dealEmailService';
  import { quotedReplyPlaintext } from '@/utils/quotedReplyExcerpt';
  import { formatDateTimeEU } from '@/utils/date';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';
  import DealEmailReplyModal from '@/components/deals/DealEmailReplyModal.vue';

  export default {
    name: 'DealEmailThreadModal',

    components: {
      LoadingSpinner,
      EmptyState,
      BaseIcon,
      Btn,
    },

    props: {
      modalId: { type: [Number, String], default: null },
      dealId: { type: String, default: null },
      threadId: { type: String, default: null },
      initialSubject: { type: String, default: '' },
      onSent: { type: Function, default: null },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const uiStore = useUIStore();
      const toastStore = useToastStore();

      const loading = ref(true);
      const thread = ref(null);

      const canReply = computed(() => !!thread.value?.id);

      const close = () => {
        if (props.modalId) uiStore.closeModal(props.modalId);
      };

      const messageIcon = (message) =>
        message?.direction === 'inbound' ? 'envelope' : 'paper-plane';

      const INLINE_IMAGE_TYPES = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'image/bmp',
      ];

      const isInlineImage = (file) =>
        INLINE_IMAGE_TYPES.includes(String(file?.content_type || '').toLowerCase());

      const messageImages = (message) =>
        (message?.attachments || []).filter((file) => file?.url && isInlineImage(file));

      const messageFiles = (message) =>
        (message?.attachments || []).filter((file) => file?.url && !isInlineImage(file));

      const messageBody = (message) => {
        const cleaned = quotedReplyPlaintext(message?.body_text, message?.body_html);
        if (cleaned) return cleaned;
        if (messageImages(message).length || messageFiles(message).length) return '';
        return $t('deals.mail_thread_no_body');
      };

      const loadThread = async () => {
        loading.value = true;
        try {
          const { threads } = await dealEmailService.getDealEmails(props.dealId);
          thread.value =
            (threads || []).find((item) => item.id === props.threadId) || null;
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('deals.mail_thread_load_failed'),
          });
          thread.value = null;
        } finally {
          loading.value = false;
        }
      };

      const handleClose = () => close();

      const handleReply = () => {
        const currentThread = thread.value;
        close();
        uiStore.addModal(DealEmailReplyModal, {
          title: $t('deals.mail_reply'),
          size: 'md',
          dealId: props.dealId,
          threadId: currentThread.id,
          initialSubject: currentThread.subject || props.initialSubject || '',
          onSent: props.onSent,
        });
      };

      onMounted(loadThread);

      return {
        loading,
        thread,
        canReply,
        formatDateTimeEU,
        messageIcon,
        messageBody,
        messageImages,
        messageFiles,
        handleClose,
        handleReply,
      };
    },
  };
</script>

<style scoped lang="scss">
  .deal-email-thread {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__loading {
      display: flex;
      justify-content: center;
      padding: 2rem 0;
    }

    &__messages {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      max-height: 60vh;
      overflow-y: auto;
    }

    &__msg {
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      padding: 0.875rem 1rem;
      background: var(--slate-50);

      &--outbound {
        background: white;
      }
    }

    &__msg-header {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      margin-bottom: 0.625rem;
    }

    &__msg-icon {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      background: var(--purple-100);
      color: var(--purple-600);

      &--outbound {
        background: var(--brand-100);
        color: var(--brand-600);
      }
    }

    &__msg-meta {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }

    &__msg-from {
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    &__msg-time {
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__msg-body {
      margin: 0;
      white-space: pre-wrap;
      word-break: break-word;
      font-family: inherit;
      font-size: 0.875rem;
      line-height: 1.5;
      color: var(--slate-700);
    }

    &__images {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-top: 0.75rem;
    }

    &__image {
      display: block;
      max-width: 220px;

      img {
        display: block;
        width: 100%;
        max-height: 220px;
        object-fit: cover;
        border-radius: 0.5rem;
        border: 1px solid var(--slate-200);
        background: white;
      }
    }

    &__files {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      margin-top: 0.75rem;
      @include fontSize('sm');

      a {
        color: var(--brand-600);
      }
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
    }
  }
</style>
